import { useAtom, useAtomValue } from 'jotai';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';

import { cdnListParamsAtom, cdnHydratedAtom } from '@/store';

export interface SearchFields {
  cdn_tag: string;
}

export function useCdnFilters() {
  const [params, setParams] = useAtom(cdnListParamsAtom);
  const [, setHydrated] = useAtom(cdnHydratedAtom);

  // URL state management
  const [urlState, setUrlState] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    search: parseAsString.withDefault(''),
    cdn_tag: parseAsString,
    date: parseAsString,
  });

  // Initialize hydrated state and sync URL with params
  useMemo(() => {
    if (!urlState.page || !urlState.per_page) return;

    const filters: Record<string, string | undefined> = {};
    if (urlState.search) filters.search = urlState.search;
    if (urlState.cdn_tag) filters.cdn_tag = urlState.cdn_tag;

    setParams({
      page: urlState.page,
      per_page: urlState.per_page,
      filters,
    });

    setHydrated(true);
  }, [urlState.page, urlState.per_page, urlState.search, urlState.cdn_tag, setParams, setHydrated]);

  // Search fields state
  const searchFields: SearchFields = {
    cdn_tag: params.filters.cdn_tag || '',
  };

  // Search state - local state for input, URL state for actual search
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.search || '');
  const searchTerm = urlState.search || '';

  const setSearchFields = useCallback((updater: (prev: SearchFields) => SearchFields) => {
    const newFields = updater(searchFields);
    setUrlState({
      cdn_tag: newFields.cdn_tag || null,
      page: 1, // Reset to first page when searching
    });
  }, [searchFields, setUrlState]);

  // Search handler
  const handleSearch = useCallback(() => {
    setUrlState({
      search: localSearchTerm || null,
      page: 1,
    });
  }, [localSearchTerm, setUrlState]);

  // Clear search handler
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    setUrlState({
      search: null,
      page: 1,
    });
  }, [setUrlState]);

  const applyFilters = useCallback(() => {
    setUrlState({
      cdn_tag: searchFields.cdn_tag || null,
      page: 1,
    });
  }, [searchFields, setUrlState]);

  const clearFilters = useCallback(() => {
    setSearchFields(() => ({ cdn_tag: '' }));
    setUrlState({
      search: null,
      cdn_tag: null,
      page: 1,
    });
  }, [setSearchFields, setUrlState]);

  const handlePageChange = useCallback((page: number) => {
    setUrlState({ page });
  }, [setUrlState]);

  return {
    searchTerm: localSearchTerm,
    setSearchTerm: setLocalSearchTerm,
    searchFields,
    setSearchFields,
    applyFilters,
    clearFilters,
    handlePageChange,
    handleSearch,
    handleClearSearch,
    hasAnyFilter: Boolean(searchTerm || searchFields.cdn_tag),
  };
}
